import React, { useMemo, useState } from 'react';
import Dropdown from '../../Dropdown';

// Utils 
import { getTotalsBook, getMoneylineBook, getSpreadBook, formatOddsAndLines, getSportsPeriodOptions } from '../../../../utils/betting';

const SportBettingData = ({game, sport, period, betType, setPeriod, setBetType}) => {
    const sportPeriodOptions = useMemo(() => getSportsPeriodOptions(sport), [sport]);
    const totalsBooks = useMemo(() => getTotalsBook(game, sport, period, 'all'), [game, sport, period]);
    const moneylineBooks = useMemo(() => getMoneylineBook(game, sport, period, 'all'), [game, sport, period]);
    const spreadBooks = useMemo(() => getSpreadBook(game, sport, period, 'all'), [game, sport, period]);

    const spreadLabel = sport === 'mlb' ? 'Runline' : 'Spread';
    const homeTeam = sport === 'mlb' ? game?.home?.team : game?.home?.team_id;
    const awayTeam = sport === 'mlb' ? game?.away?.team : game?.away?.team_id;
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4 relative mt-1 ">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Betting Lines</h3>
            </div>
            <div className="w-full flex flex-col md:flex-row items-center justify-between mb-4 relative mt-1 gap-3">
                <Dropdown
                    id="bet-type-select"
                    title="Select Bet Type"
                    items={[
                        { value: 'totals', label: 'Totals' },
                        { value: 'spread', label: spreadLabel },
                        { value: 'ml', label: 'Moneyline' }
                    ]}
                    activeItem={betType}
                    onSelect={setBetType}
                    size="md"
                    variant="default"
                />
                <Dropdown
                    id="period-select"
                    title="Select Game Period"
                    items={sportPeriodOptions}
                    activeItem={period}
                    onSelect={setPeriod}
                    size="md"
                    variant="default"
                />
            </div>
            
            {betType === "totals" && (
                totalsBooks?.length > 0 ? (
                    <>
                        {/* Table view for md and below */}
                        <div className="lg:hidden border border-gray-200 dark:border-gray-600 rounded-lg">
                            <table className="min-w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Book</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Line</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Over</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Under</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {totalsBooks.map((book) => (
                                        <tr key={book.book}>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{book.book}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{book.line}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.over)}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.under)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Card view for lg and above */}
                        <div className="hidden lg:flex gap-4 py-4">
                            {/* Column labels */}
                            <div className="flex flex-col gap-3 min-w-[120px]">
                                <div className="h-12 px-4 " />
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">Line</div>
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">Over</div>
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">Under</div>
                            </div>
                            
                            {/* Book cards */}
                            <div className="flex-1 flex gap-4 overflow-x-auto">
                                {totalsBooks.map((book) => (
                                    <div key={book.book} className="flex flex-col gap-3 min-w-[140px] flex-shrink-0">
                                        <div className="h-12 flex items-center px-4 border-b border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 font-medium">{book.book}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{book.line}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.over)}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.under)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">Odds unavailable</div>
                )
            )}
            
            {betType === "ml" && (
                moneylineBooks?.length > 0 ? (
                    <>
                        {/* Table view for md and below */}
                        <div className="lg:hidden border border-gray-200 dark:border-gray-600 rounded-lg">
                            <table className="min-w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Book</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{homeTeam}</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{awayTeam}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {moneylineBooks.map((book) => (
                                        <tr key={book.book}>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{book.book}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.home)}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.away)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Card view for lg and above */}
                        <div className="hidden lg:flex gap-4 py-4">
                            {/* Column labels */}
                            <div className="flex flex-col gap-3 min-w-[120px]">
                                <div className="h-12 px-4 " />
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">{homeTeam}</div>
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">{awayTeam}</div>
                            </div>
                            
                            {/* Book cards */}
                            <div className="flex-1 flex gap-4 overflow-x-auto">
                                {moneylineBooks.map((book) => (
                                    <div key={book.book} className="flex flex-col gap-3 min-w-[140px] flex-shrink-0">
                                        <div className="h-12 flex items-center px-4 border-b border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 font-medium">{book.book}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.home)}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{formatOddsAndLines(book.away)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">Odds unavailable</div>
                )
            )}
            
            {betType === "spread" && (
                spreadBooks?.length > 0 ? (
                    <>
                        {/* Table view for md and below */}
                        <div className="lg:hidden border border-gray-200 dark:border-gray-600 rounded-lg">
                            <table className="min-w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Book</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{homeTeam}</th>
                                        <th className="border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{awayTeam}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {spreadBooks.map((book) => (
                                        <tr key={book.book}>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{book.book}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{`${formatOddsAndLines(book.home.points)} (${formatOddsAndLines(book.home.odds)})`}</td>
                                            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">{`${formatOddsAndLines(book.away.points)} (${formatOddsAndLines(book.away.odds)})`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Card view for lg and above */}
                        <div className="hidden lg:flex gap-4 py-4">
                            {/* Column labels */}
                            <div className="flex flex-col gap-3 min-w-[120px]">
                                <div className="h-12 px-4 " />
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">{homeTeam}</div>
                                <div className="h-12 flex items-center px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg font-semibold text-sm text-gray-900 dark:text-gray-100">{awayTeam}</div>
                            </div>
                            
                            {/* Book cards */}
                            <div className="flex-1 flex gap-4 overflow-x-auto">
                                {spreadBooks.map((book) => (
                                    <div key={book.book} className="flex flex-col gap-3 min-w-[140px] flex-shrink-0">
                                        <div className="h-12 flex items-center px-4 border-b border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 font-medium">{book.book}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{`${formatOddsAndLines(book.home.points)} (${formatOddsAndLines(book.home.odds)})`}</div>
                                        <div className="h-12 flex items-center px-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100">{`${formatOddsAndLines(book.away.points)} (${formatOddsAndLines(book.away.odds)})`}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">Odds unavailable</div>
                )
            )}
        </div>
    );
};

export default SportBettingData;